import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DOCUMENT,
  inject,
  input,
  linkedSignal,
  output,
} from '@angular/core';
import { ImageCrop, ImageFormat } from '../../../types';
import { IconUIComponent } from '../icon/icon.component';
import { buildImageUrl } from './_utils/build-image-url';

type ImageStatus = 'loading' | 'loaded' | 'error';

/**
 * An image from storage, requested at the size it is shown at. Until it loads, when it fails and
 * when there is no `src`, a preview fills the box: a generic picture icon, or the host's own
 * `[preview]` content. The host sizes the component; the image covers it. The preview background
 * is `--ui-image-preview-bg` (gray by default) so a coloured host can show through it.
 */
@Component({
  selector: 'fr8bot-ui-image',
  imports: [IconUIComponent],
  templateUrl: './image.component.html',
  styleUrl: './image.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImageUIComponent {
  private readonly pixelRatio = inject(DOCUMENT).defaultView?.devicePixelRatio ?? 1;

  readonly src = input<string | null | undefined>(null);
  readonly alt = input('');
  /** Longest side in CSS pixels. Ignored when `width` or `height` is set. */
  readonly size = input<number>();
  readonly width = input<number>();
  readonly height = input<number>();
  readonly crop = input<ImageCrop>('none');
  readonly format = input<ImageFormat>('webp');
  readonly quality = input<number>();
  /** The untouched upload, for fullscreen viewing. */
  readonly original = input(false);
  readonly fit = input<'cover' | 'contain'>('cover');
  readonly lazy = input(true);

  /** The image could not be loaded; the preview stays in its place. */
  readonly failed = output<void>();

  protected readonly url = computed(() =>
    buildImageUrl(
      this.src(),
      {
        size: this.size(),
        width: this.width(),
        height: this.height(),
        crop: this.crop(),
        format: this.format(),
        quality: this.quality(),
        original: this.original(),
      },
      this.pixelRatio,
    ),
  );

  protected readonly status = linkedSignal<string | null, ImageStatus>({
    source: this.url,
    computation: (url) => (url ? 'loading' : 'error'),
  });
}
